import { Injectable, signal } from '@angular/core';
import { ClipMode } from '../models/clip.model';

export type RecordSessionStep = 'idle' | 'recording' | 'reviewing' | 'uploading';

/** Por qué no arrancó la grabación: clave de traducción y, si lo hay, el nombre técnico del fallo. */
export interface FalloAlArrancar {
  clave: string;
  motivo?: string;
}

/**
 * Lo que se espera a que el navegador conteste al selector de pantalla antes de devolver el
 * control (M05.04 RF-001, INC-0097 §2).
 *
 * <p>`getDisplayMedia` puede quedarse pendiente para siempre —ni resuelve ni falla— cuando el
 * selector no llega a abrirse. Pasado este plazo se suelta el botón y se dice qué mirar, en vez
 * de dejar la ventana muerta: la promesa sigue viva por si el navegador despierta, pero quien
 * grababa ya puede volver a intentarlo.
 */
const ESPERA_DEL_SELECTOR_MS = 12_000;

/**
 * Estado de una grabación de Clip en curso, fuera del ciclo de vida de cualquier componente
 * ruteado (RN: antes vivía en `RecordModeModalComponent`, que el router destruye al cambiar de
 * módulo — su `DestroyRef.onDestroy` paraba el `MediaRecorder` y cortaba todas las pistas del
 * stream, así que "compartir pantalla" desde Clips se cortaba en cuanto la persona navegaba a
 * otro módulo). `providedIn: 'root'` = un solo singleton para toda la sesión de la SPA, ajeno a
 * qué ruta esté activa; el componente flotante en `AppComponent` y el modal de Clips solo LEEN
 * este estado, nunca lo destruyen al desmontarse.
 */
@Injectable({ providedIn: 'root' })
export class ClipRecordingService {
  readonly step = signal<RecordSessionStep>('idle');
  readonly isPaused = signal(false);
  readonly elapsedSeconds = signal(0);
  readonly recordedBlob = signal<Blob | null>(null);
  readonly previewUrl = signal<string | null>(null);
  /**
   * Por qué no arrancó el último intento, para decírselo a quien lo intentó.
   *
   * <p>Lleva una clave de traducción y no un texto: el mensaje lo pinta el modal, que es quien
   * sabe de idioma. Antes era una sola frase en español para cualquier causa —navegador sin
   * soporte, permiso denegado, sin dispositivo— y el error real se descartaba sin mirarlo, así
   * que ni la persona sabía qué hacer ni quedaba rastro para averiguarlo después.
   */
  readonly startError = signal<FalloAlArrancar | null>(null);
  readonly isStarting = signal(false);

  /** Identifica el intento en curso: lo que conteste un intento ya abandonado se descarta. */
  private sesionDeArranque = 0;

  /**
   * Micrófonos disponibles para elegir antes de grabar (modos 'camera' y 'both').
   *
   * <p>Sin esto, `getUserMedia({ audio: true })` deja que el navegador/SO elija el dispositivo
   * "por defecto" — típicamente el micrófono integrado del equipo. Alguien con un micrófono
   * externo por cable o Bluetooth no tenía forma de decirle a la app que use ESE en vez del
   * integrado (o de ningún micrófono, si el equipo no tiene uno propio).
   */
  readonly microphones = signal<MediaDeviceInfo[]>([]);
  readonly selectedMicId = signal<string | null>(null);

  /**
   * Micrófono silenciado durante la grabación (RF-004).
   *
   * <p>No se para la pista ni se rehace la mezcla: se le quita el `enabled`, que es lo que deja
   * la pista viva emitiendo silencio. Pararla obligaría a reconstruir el `AudioContext` a mitad
   * de la grabación, y volver a pedir el micrófono dispararía otra vez el permiso del navegador.
   */
  readonly micMuted = signal(false);

  /** Si hay micrófono en esta grabación. Sin él, el botón de silenciar no tiene nada que apagar. */
  readonly hasMic = signal(false);

  /** Las pistas del micrófono de la grabación en curso, antes de mezclarse. */
  private micTracks: MediaStreamTrack[] = [];
  private devicesHandler: (() => void) | null = null;

  /**
   * Lista los micrófonos del sistema. Las etiquetas (`label`) sólo llegan legibles si el origen
   * ya tiene permiso de micrófono/cámara concedido de antes; si no, `enumerateDevices` igual
   * devuelve los dispositivos pero con `label` vacío -- se resuelve con un rótulo genérico
   * numerado en el componente, el `deviceId` sigue siendo válido para seleccionar.
   *
   * <p>Escucha `devicechange` para que un micrófono Bluetooth emparejado DESPUÉS de abrir el
   * modal (caso común: recién se prende el dispositivo) aparezca sin tener que cerrar y reabrir.
   */
  async loadMicrophones(): Promise<void> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.microphones.set(devices.filter(d => d.kind === 'audioinput'));
    } catch {
      this.microphones.set([]);
    }

    // Un solo listener para toda la sesión de la SPA (este servicio es `providedIn: 'root'`,
    // vive tanto como la pestaña): no hay un punto natural de "cerrar" donde desengancharlo.
    if (!this.devicesHandler) {
      this.devicesHandler = () => { void this.loadMicrophones(); };
      navigator.mediaDevices.addEventListener('devicechange', this.devicesHandler);
    }
  }

  /** Contexto de la grabación activa — necesario para subir el clip aunque el usuario haya
   *  vuelto a entrar a Clips desde otra pestaña/espacio mientras grababa. */
  readonly title = signal('');
  readonly selectedMode = signal<ClipMode>('screen');
  readonly entityType = signal('space');
  readonly entityId = signal(0);

  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private timerHandle: ReturnType<typeof setInterval> | null = null;
  private previewVideoEl: HTMLVideoElement | null = null;

  // ── Composición pantalla+cámara (modo 'both') y mezcla de audio ───────────
  // `stream` (lo que recibe el MediaRecorder) puede ser un canvas.captureStream() con una pista
  // de audio mezclada — ninguna de las dos está "viva" por sí sola: hay que parar también las
  // fuentes crudas (pantalla, cámara, micrófono) y el AudioContext, o quedan cámaras/micrófonos
  // encendidos y el indicador de "compartiendo pantalla" del navegador nunca se apaga.
  private rawStreams: MediaStream[] = [];
  private compositeRafId: number | null = null;
  private compositeVideos: HTMLVideoElement[] = [];
  private audioCtx: AudioContext | null = null;

  get stream(): MediaStream | null {
    return this.mediaStream;
  }

  /** Conecta (o reconecta, p. ej. al reabrir el modal) el `<video>` de vista previa al stream vivo. */
  attachPreview(video: HTMLVideoElement | null): void {
    this.previewVideoEl = video;
    if (video && this.mediaStream) video.srcObject = this.mediaStream;
  }

  async start(mode: ClipMode, title: string, entityType: string, entityId: number): Promise<{ ok: true } | { ok: false; error: string }> {
    this.startError.set(null);
    const sesion = ++this.sesionDeArranque;

    const sinSoporte = this.loQueElNavegadorNoOfrece(mode);
    if (sinSoporte) {
      this.startError.set({ clave: sinSoporte });
      return { ok: false, error: sinSoporte };
    }

    this.isStarting.set(true);
    this.cleanupComposite(); // por si quedó algo colgado de un intento anterior fallido
    const sueltaElBoton = setTimeout(() => this.devolverElControl(sesion), ESPERA_DEL_SELECTOR_MS);
    try {
      // deviceId "exact": pedirle al navegador ESE micrófono puntual (cable o Bluetooth ya
      // emparejado), no el que el SO tenga marcado como predeterminado. `null` = comportamiento
      // de siempre (el navegador/SO elige).
      //
      // echoCancellation/noiseSuppression/autoGainControl explícitos: con `audio: true` a secas
      // algunos navegadores/dispositivos los dejan en su valor por defecto del hardware, no
      // necesariamente activados — de ahí que se "escuche" la propia voz (eco) o entrecortada al
      // mezclar con el audio de pantalla. Pedirlos explícitamente se lo exige al navegador.
      const micId = this.selectedMicId();
      const audioConstraint: MediaTrackConstraints = micId
        ? { deviceId: { exact: micId }, echoCancellation: true, noiseSuppression: true, autoGainControl: true }
        : { echoCancellation: true, noiseSuppression: true, autoGainControl: true };

      let stream: MediaStream;
      /** Pista de vídeo de la pantalla compartida cruda — para escuchar "ended" cuando el
       *  navegador la corta (botón nativo "Dejar de compartir"). En 'both' la pista que graba
       *  el MediaRecorder es la del canvas compuesto, que nunca dispara ese evento por sí sola. */
      let screenTrackForEndedEvent: MediaStreamTrack | null = null;

      if (mode === 'camera') {
        // Cámara sola: una única llamada ya entrega vídeo+audio en el mismo stream, sin nada que mezclar.
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: audioConstraint });
        // Aquí la pista de audio del propio stream ES la del micrófono (RF-004).
        this.micTracks = stream.getAudioTracks();
      } else if (mode === 'screen') {
        const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        this.rawStreams.push(displayStream);
        screenTrackForEndedEvent = displayStream.getVideoTracks()[0] ?? null;

        // Antes esto no existía: "solo pantalla" nunca pedía el micrófono, así que el clip
        // siempre salía mudo salvo que el navegador compartiera audio de la pestaña.
        const micStream = await this.tryGetMic(audioConstraint);
        // Se guardan ANTES de mezclar: después sólo queda la pista mezclada, y silenciarla
        // apagaría también el audio de la pantalla (RF-004).
        this.micTracks = micStream?.getAudioTracks() ?? [];
        const mixedAudio = await this.mixAudioTracks([...displayStream.getAudioTracks(), ...(micStream?.getAudioTracks() ?? [])]);

        stream = new MediaStream([
          ...(screenTrackForEndedEvent ? [screenTrackForEndedEvent] : []),
          ...(mixedAudio ? [mixedAudio] : []),
        ]);
      } else {
        // 'both': pantalla + cámara en PiP sobre un <canvas>, con el audio de pantalla y el del
        // micrófono mezclados en una sola pista. Antes se subía tal cual el stream de pantalla,
        // sin cámara ni micrófono — el "PiP" del icono del modo no llegaba a grabarse nunca.
        const displayStream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
        this.rawStreams.push(displayStream);
        screenTrackForEndedEvent = displayStream.getVideoTracks()[0] ?? null;

        const camStream = await this.tryGetCamera();
        const micStream = await this.tryGetMic(audioConstraint);
        this.micTracks = micStream?.getAudioTracks() ?? [];

        const compositeVideoTrack = this.composePictureInPicture(displayStream, camStream);
        const mixedAudio = await this.mixAudioTracks([...displayStream.getAudioTracks(), ...(micStream?.getAudioTracks() ?? [])]);

        stream = new MediaStream([
          compositeVideoTrack,
          ...(mixedAudio ? [mixedAudio] : []),
        ]);
      }

      this.mediaStream = stream;
      // Si el selector tardó tanto que ya se soltó el botón y se empezó otro intento, este stream
      // sobra: se suelta entero en vez de ponerse a grabar por detrás del que la persona ve.
      if (sesion !== this.sesionDeArranque) {
        stream.getTracks().forEach(track => track.stop());
        this.cleanupComposite();
        return { ok: false, error: 'nexus.collaboration.elSelectorNoSeAbrio' };
      }

      // El micrófono es opcional: si el usuario lo negó o no hay dispositivo, se graba sin él y
      // el botón de silenciar no se ofrece en vez de quedarse muerto.
      this.hasMic.set(this.micTracks.length > 0);
      this.micMuted.set(false);
      this.cortadaPorDuracion.set(false);
      this.title.set(title);
      this.selectedMode.set(mode);
      this.entityType.set(entityType);
      this.entityId.set(entityId);
      this.isStarting.set(false);
      this.step.set('recording');

      if (this.previewVideoEl) this.previewVideoEl.srcObject = stream;

      this.recordedChunks = [];
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
      recorder.ondataavailable = (evt: BlobEvent) => {
        if (evt.data.size > 0) this.recordedChunks.push(evt.data);
      };
      recorder.onstop = () => this.onRecorderStopped();
      recorder.start();
      this.mediaRecorder = recorder;

      // Si el usuario detiene el compartir pantalla desde el propio navegador.
      (screenTrackForEndedEvent ?? stream.getVideoTracks()[0])?.addEventListener('ended', () => this.stop());

      this.elapsedSeconds.set(0);
      this.isPaused.set(false);
      this.timerHandle = setInterval(() => this.contarUnSegundo(), 1000);
      return { ok: true };
    } catch (err: unknown) {
      this.isStarting.set(false);
      this.cleanupComposite();
      this.mediaStream?.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
      const fallo = this.porQueNoArranco(err);
      this.startError.set(fallo);
      return { ok: false, error: fallo.clave };
    } finally {
      clearTimeout(sueltaElBoton);
    }
  }

  /**
   * La clave del aviso cuando el navegador no ofrece lo que este modo necesita, o `null` si sí.
   *
   * <p>`navigator.mediaDevices` no existe fuera de un contexto seguro, y `getDisplayMedia` falta
   * en navegadores que no capturan pantalla. Sin esta comprobación el fallo llegaba al mismo
   * mensaje genérico de «verifica los permisos», que manda a mirar donde no es.
   */
  private loQueElNavegadorNoOfrece(mode: ClipMode): string | null {
    const medios = navigator.mediaDevices as MediaDevices | undefined;
    if (!medios?.getUserMedia) return 'nexus.collaboration.elNavegadorNoPermiteGrabar';
    if (mode !== 'camera' && !medios.getDisplayMedia) return 'nexus.collaboration.elNavegadorNoCapturaPantalla';
    return null;
  }

  /**
   * Suelta el botón cuando el navegador no contesta, sin cancelar nada.
   *
   * <p>La promesa de `getDisplayMedia` no se puede abortar: lo único que está en nuestra mano es
   * dejar de esperarla en silencio. Si acaba despertando, el propio `start` ve que su sesión ya
   * no es la vigente y suelta lo que le hayan dado.
   */
  private devolverElControl(sesion: number): void {
    if (sesion !== this.sesionDeArranque || !this.isStarting()) return;
    this.isStarting.set(false);
    this.startError.set({ clave: 'nexus.collaboration.elSelectorNoSeAbrio' });
  }

  /**
   * Traduce el fallo del navegador a algo que se pueda leer y, sobre todo, accionar.
   *
   * <p>El nombre técnico viaja aparte para que quede dicho en pantalla: es lo que hacía falta para
   * diagnosticar esta incidencia y lo que antes se descartaba en un `catch` vacío.
   */
  private porQueNoArranco(err: unknown): FalloAlArrancar {
    const motivo = err instanceof Error ? err.name : undefined;
    switch (motivo) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return { clave: 'nexus.collaboration.noSeCompartioNingunaPantalla', motivo };
      case 'NotFoundError':
      case 'DevicesNotFoundError':
      case 'OverconstrainedError':
        return { clave: 'nexus.collaboration.sinDispositivoParaGrabar', motivo };
      case 'NotReadableError':
        return { clave: 'nexus.collaboration.elDispositivoEstaOcupado', motivo };
      default:
        return { clave: 'nexus.collaboration.noSePudoIniciarLaGrabacion', motivo: motivo ?? '—' };
    }
  }

  /** Micrófono opcional: si el usuario lo niega o no hay dispositivo, se graba sin él en vez de fallar todo el clip. */
  private async tryGetMic(audioConstraint: MediaTrackConstraints | boolean): Promise<MediaStream | null> {
    try {
      const micStream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraint });
      this.rawStreams.push(micStream);
      return micStream;
    } catch {
      return null;
    }
  }

  /** Cámara opcional para el PiP de 'both': si falla, se graba igual solo con la pantalla. */
  private async tryGetCamera(): Promise<MediaStream | null> {
    try {
      const camStream = await navigator.mediaDevices.getUserMedia({ video: { width: 320, height: 240 } });
      this.rawStreams.push(camStream);
      return camStream;
    } catch {
      return null;
    }
  }

  /**
   * Mezcla N pistas de audio en una sola vía `AudioContext` — `MediaRecorder` no tiene un
   * comportamiento definido para grabar con dos pistas de audio en el mismo `MediaStream` (antes
   * se le pasaban pantalla+micrófono como dos pistas sueltas y, según el navegador, una de las
   * dos se perdía en silencio). Con una sola pista no hace falta mezclar nada.
   *
   * <p><b>`resume()` explícito.</b> Varios `await` (getDisplayMedia/getUserMedia) ya pasaron desde
   * el clic que arrancó todo esto, y Chrome puede haber invalidado la "activación transitoria" del
   * usuario para cuando se llega aquí — un `AudioContext` nuevo nace entonces `suspended`, y sin
   * este `resume()` la pista mezclada arranca en silencio o entrecortada (algunos navegadores la
   * empiezan a procesar recién en el primer `resume`, a medias) hasta que algo más la reanude.
   */
  private async mixAudioTracks(tracks: MediaStreamTrack[]): Promise<MediaStreamTrack | null> {
    if (tracks.length === 0) return null;
    if (tracks.length === 1) return tracks[0];

    const ctx = new AudioContext();
    this.audioCtx = ctx;
    if (ctx.state === 'suspended') await ctx.resume();
    const destination = ctx.createMediaStreamDestination();
    for (const track of tracks) {
      ctx.createMediaStreamSource(new MediaStream([track])).connect(destination);
    }
    return destination.stream.getAudioTracks()[0] ?? null;
  }

  /**
   * Dibuja la pantalla a pantalla completa y la cámara como miniatura ("picture-in-picture") en
   * un `<canvas>` oculto, cuadro a cuadro, y devuelve la pista de vídeo resultante de
   * `canvas.captureStream()` para pasársela al `MediaRecorder`. Sin cámara disponible, dibuja
   * solo la pantalla (el modo sigue siendo útil, solo pierde el PiP).
   */
  private composePictureInPicture(displayStream: MediaStream, camStream: MediaStream | null): MediaStreamTrack {
    const screenVideo = document.createElement('video');
    screenVideo.muted = true;
    screenVideo.playsInline = true;
    screenVideo.srcObject = displayStream;
    void screenVideo.play();

    let camVideo: HTMLVideoElement | null = null;
    if (camStream) {
      camVideo = document.createElement('video');
      camVideo.muted = true;
      camVideo.playsInline = true;
      camVideo.srcObject = camStream;
      void camVideo.play();
    }

    const canvas = document.createElement('canvas');
    canvas.width = 1280;
    canvas.height = 720;
    const ctx = canvas.getContext('2d');

    const draw = () => {
      if (ctx) {
        const w = screenVideo.videoWidth || canvas.width;
        const h = screenVideo.videoHeight || canvas.height;
        if (canvas.width !== w || canvas.height !== h) {
          canvas.width = w;
          canvas.height = h;
        }
        ctx.drawImage(screenVideo, 0, 0, canvas.width, canvas.height);

        if (camVideo && camVideo.videoWidth) {
          const pipWidth = Math.round(canvas.width * 0.22);
          const pipHeight = Math.round(pipWidth * (camVideo.videoHeight / camVideo.videoWidth));
          const margin = Math.round(canvas.width * 0.02);
          const x = canvas.width - pipWidth - margin;
          const y = canvas.height - pipHeight - margin;
          ctx.save();
          ctx.beginPath();
          ctx.rect(x, y, pipWidth, pipHeight);
          ctx.clip();
          ctx.drawImage(camVideo, x, y, pipWidth, pipHeight);
          ctx.restore();
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
          ctx.lineWidth = Math.max(2, Math.round(canvas.width * 0.003));
          ctx.strokeRect(x, y, pipWidth, pipHeight);
        }
      }
      this.compositeRafId = requestAnimationFrame(draw);
    };
    this.compositeRafId = requestAnimationFrame(draw);

    this.compositeVideos = camVideo ? [screenVideo, camVideo] : [screenVideo];
    return canvas.captureStream(30).getVideoTracks()[0];
  }

  /** Para el `requestAnimationFrame` del canvas, libera los `<video>` internos y cierra el `AudioContext` de mezcla. */
  private cleanupComposite(): void {
    if (this.compositeRafId !== null) {
      cancelAnimationFrame(this.compositeRafId);
      this.compositeRafId = null;
    }
    this.compositeVideos.forEach(v => {
      v.pause();
      v.srcObject = null;
    });
    this.compositeVideos = [];
    if (this.audioCtx) {
      void this.audioCtx.close();
      this.audioCtx = null;
    }
    this.rawStreams.forEach(s => s.getTracks().forEach(track => track.stop()));
    this.rawStreams = [];
    // Las pistas del micrófono viven dentro de `rawStreams`: ya están paradas, y conservar la
    // referencia sólo serviría para que la grabación siguiente creyera que tiene micrófono.
    this.micTracks = [];
    this.micMuted.set(false);
    this.hasMic.set(false);
  }

  /**
   * Duración máxima del clip, en segundos (RF-006).
   *
   * <p>La fija el Admin del tenant y la sirve el backend; aquí se guarda el valor vigente para
   * cortar al llegar. Mientras no llegue, vale el defecto del RF —diez minutos—: empezar sin tope
   * y que la grabación no se corte nunca sería peor que cortar en el defecto.
   */
  readonly maxDuracionSegundos = signal(600);

  /** Avisa de que la grabación se cortó sola por llegar al máximo, para poder decirlo. */
  readonly cortadaPorDuracion = signal(false);

  /**
   * Un segundo de grabación. Corta sola al llegar al máximo (RF-006).
   *
   * <p>«Al llegar al máximo, la grabación se detiene automáticamente y se procesa»: se llama a
   * {@link #stop}, el mismo camino que el botón de detener, para que el clip quede en revisión y no
   * se pierda. Antes no existía ningún tope: se grababa hasta que la persona paraba, y un
   * despiste dejaba un fichero de horas contra la cuota del tenant.
   */
  private contarUnSegundo(): void {
    const transcurrido = this.elapsedSeconds() + 1;
    this.elapsedSeconds.set(transcurrido);
    if (transcurrido >= this.maxDuracionSegundos()) {
      this.cortadaPorDuracion.set(true);
      this.stop();
    }
  }

  /**
   * Silencia o reactiva el micrófono sin cortar la grabación (RF-004).
   *
   * <p>Lo que se apaga es la pista del micrófono, no la mezclada: en pantalla y en pantalla+cámara
   * el audio que graba el `MediaRecorder` lleva también el de la pantalla, y silenciar la mezcla
   * dejaría el clip mudo del todo.
   */
  toggleMic(): void {
    if (this.micTracks.length === 0) return;
    const silenciado = !this.micMuted();
    this.micMuted.set(silenciado);
    for (const track of this.micTracks) track.enabled = !silenciado;
  }

  togglePause(): void {
    const recorder = this.mediaRecorder;
    if (!recorder || recorder.state === 'inactive') return;
    if (recorder.state === 'paused') {
      recorder.resume();
      this.isPaused.set(false);
      this.timerHandle = setInterval(() => this.contarUnSegundo(), 1000);
    } else {
      recorder.pause();
      this.isPaused.set(true);
      if (this.timerHandle) {
        clearInterval(this.timerHandle);
        this.timerHandle = null;
      }
    }
  }

  stop(): void {
    if (this.timerHandle) {
      clearInterval(this.timerHandle);
      this.timerHandle = null;
    }
    this.isPaused.set(false);
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.mediaStream = null;
    // El stream que graba el MediaRecorder puede ser un canvas+audio mezclado (modo 'both'):
    // pararlo no apaga la cámara/pantalla/micrófono reales que lo alimentan.
    this.cleanupComposite();
  }

  private onRecorderStopped(): void {
    const blob = new Blob(this.recordedChunks, { type: 'video/webm' });
    this.recordedBlob.set(blob);
    this.previewUrl.set(URL.createObjectURL(blob));
    this.step.set('reviewing');
  }

  discardAndRetry(): void {
    const url = this.previewUrl();
    if (url) URL.revokeObjectURL(url);
    this.previewUrl.set(null);
    this.recordedBlob.set(null);
    this.recordedChunks = [];
    this.elapsedSeconds.set(0);
    this.step.set('idle');
  }

  setUploading(): void {
    this.step.set('uploading');
  }

  backToReview(): void {
    this.step.set('reviewing');
  }

  /** Tras subir con éxito, o al cancelar del todo antes de empezar: listo para una nueva grabación. */
  reset(): void {
    if (this.timerHandle) {
      clearInterval(this.timerHandle);
      this.timerHandle = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.mediaStream?.getTracks().forEach(track => track.stop());
    this.mediaStream = null;
    this.cleanupComposite();
    const url = this.previewUrl();
    if (url) URL.revokeObjectURL(url);
    this.previewUrl.set(null);
    this.recordedBlob.set(null);
    this.recordedChunks = [];
    this.elapsedSeconds.set(0);
    this.isPaused.set(false);
    this.startError.set(null);
    this.step.set('idle');
  }
}
